import { Accept, useDropzone } from 'react-dropzone';

import { isPdfPasswordProtected } from '@helpers/isPdfPasswordProtected';
import { useToaster } from '@stores/toaster';

import { useBulkUploadActions, useBulkUploadState } from '../../context';
import { useCreateRecordingItems } from '../../modals/useCreateRecordingItems';

export const useDrop = (supportedFileTypes: Accept) => {
  const { addItems } = useBulkUploadActions();
  const { studyId } = useBulkUploadState();

  const showToast = useToaster();

  const { createItems } = useCreateRecordingItems({ studyId });

  const isFileAllowed = (file: File) => {
    const mimeType = file.type;
    const hasValidMimeType = Object.keys(supportedFileTypes).includes(mimeType);
    const hasValidExtension = Object.values(supportedFileTypes).some((extensions) =>
      extensions.some((ext) => file.name.toLowerCase().endsWith(ext))
    );

    return hasValidMimeType || hasValidExtension;
  };

  const handleDrop = async (files: File[]) => {
    const badFiles = files.filter((f) => !isFileAllowed(f));
    let goodFiles = files.filter((f) => isFileAllowed(f));

    if (badFiles.length > 0) {
      let heading = "We can't upload that file.";

      if (badFiles.length > 1) {
        heading = "We can't upload some of those files";
      }

      const badExtensions = Array.from(
        new Set(badFiles.map((f) => f.name.split('.').pop()?.toUpperCase()).filter(Boolean) as string[])
      ).join(', ');

      showToast({
        heading,
        text: `The following file types are not supported: ${badExtensions}.`,
        icon: 'error',
        timeoutSeconds: 8
      });
    }

    // Filter out password-protected PDFs
    const pdfs = goodFiles.filter((f) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));

    if (pdfs.length) {
      try {
        const protectedFlags = await Promise.all(pdfs.map(isPdfPasswordProtected));
        const protectedSet = new Set(pdfs.filter((_, i) => protectedFlags[i]));

        if (protectedSet.size) {
          const names = Array.from(protectedSet)
            .map((f) => f.name)
            .join(', ');
          showToast({
            heading: "We can't upload some of those files",
            text: `These PDF files are password-protected: ${names}. Remove passwords and try again.`,
            icon: 'error',
            timeoutSeconds: 8
          });

          goodFiles = goodFiles.filter((f) => !protectedSet.has(f));
        }
      } catch (error) {
        console.error('Error checking PDF password protection:', error);
        showToast({
          heading: 'We encountered an issue processing some files',
          text: 'There was a problem checking if PDFs are password-protected. Please try again or contact support if the issue persists.',
          icon: 'error',
          timeoutSeconds: 8
        });
      }
    }

    if (goodFiles.length > 0) {
      await createItems(goodFiles).then(addItems);
    }
  };

  return useDropzone({
    onDrop: handleDrop,
    noClick: true,
    accept: supportedFileTypes
  });
};
